import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReportViewer, { type EmbedConfig } from "../components/ReportViewer";
import PaginatedReport from "./PaginatedReport";
import type { ReportTileMeta, ReportsResponse } from "../reports";

type ViewState =
  | { kind: "validating" }
  | { kind: "not-found" }
  | { kind: "loading"; report: ReportTileMeta }
  | { kind: "loaded"; report: ReportTileMeta; embed: EmbedConfig }
  | { kind: "paginated"; report: ReportTileMeta }
  | { kind: "forbidden"; report: ReportTileMeta }
  | { kind: "error"; report: ReportTileMeta; message: string };

export default function Report() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<ViewState>({ kind: "validating" });

  // Phase 1: validate slug against /api/reports.
  useEffect(() => {
    let cancelled = false;
    setState({ kind: "validating" });
    (async () => {
      try {
        const r = await fetch("/api/reports");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as ReportsResponse;
        if (cancelled) return;
        const match = data.reports.find(rep => rep.slug === slug);
        if (!match) {
          setState({ kind: "not-found" });
        } else if (match.embedType === "paginated") {
          // Paginated reports use the export-to-PDF path (no live embed-info
          // fetch); the branch lives entirely in PaginatedReport.
          setState({ kind: "paginated", report: match });
        } else {
          setState({ kind: "loading", report: match });
        }
      } catch (e) {
        if (cancelled) return;
        // Treat catalog-fetch failure as not-found for UX simplicity; logs
        // the underlying message in the console for diagnostics.
        console.error("Catalog fetch failed during slug validation:", e);
        setState({ kind: "not-found" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Phase 2: once slug is validated, fetch embed-info.
  useEffect(() => {
    if (state.kind !== "loading") return;
    let cancelled = false;
    const report = state.report;
    (async () => {
      try {
        const r = await fetch(`/api/embed-info?report=${encodeURIComponent(report.slug)}`);
        if (r.status === 403) {
          if (!cancelled) setState({ kind: "forbidden", report });
          return;
        }
        if (r.status === 404) {
          if (!cancelled) setState({ kind: "not-found" });
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const embed = (await r.json()) as EmbedConfig;
        if (!cancelled) setState({ kind: "loaded", report, embed });
      } catch (e) {
        if (!cancelled) {
          setState({
            kind: "error",
            report,
            message: e instanceof Error ? e.message : String(e)
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [state.kind, state.kind === "loading" ? state.report.slug : null]);

  if (state.kind === "not-found") {
    return (
      <main
        style={{
          padding: "1.5rem",
          fontFamily: "system-ui, sans-serif",
          maxWidth: "1600px",
          margin: "0 auto"
        }}
        data-testid="report-not-found"
      >
        <h1>Report not found</h1>
        <p>The report you're looking for doesn't exist or you don't have access to it.</p>
        <Link to="/" style={{ color: "#0078d4", textDecoration: "none" }}>← Back to reports</Link>
      </main>
    );
  }

  const reportName =
    state.kind === "loaded" ? state.embed.reportName :
    state.kind === "loading" || state.kind === "error" || state.kind === "forbidden" || state.kind === "paginated" ? state.report.name :
    "Loading…";

  return (
    <main
      style={{
        padding: "0.75rem 1.5rem 1rem",
        fontFamily: "system-ui, sans-serif",
        height: "calc(100vh - 3rem)",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <nav
        style={{ fontSize: "0.85rem", marginBottom: "0.75rem" }}
        data-testid="breadcrumb"
      >
        <Link to="/" style={{ color: "#0078d4", textDecoration: "none" }}>← Reports</Link>
        <span style={{ color: "#888", margin: "0 0.5rem" }}>/</span>
        <span style={{ color: "#333" }}>{reportName}</span>
      </nav>
      {state.kind === "forbidden" && (
        <p style={{ color: "#b00020" }} data-testid="report-forbidden">
          You don't have access to this report. Contact your administrator if you expected access.
        </p>
      )}
      {state.kind === "error" && (
        <p style={{ color: "#b00020" }} data-testid="embed-error">
          Couldn't load report: {state.message}
        </p>
      )}
      {(state.kind === "validating" || state.kind === "loading") && (
        <p style={{ color: "#555" }} data-testid="embed-loading">
          Loading report…
        </p>
      )}
      {state.kind === "loaded" && <ReportViewer config={state.embed} />}
      {state.kind === "paginated" && <PaginatedReport report={state.report} />}
    </main>
  );
}
